import { useEffect, useRef, useState, type ReactNode } from "react";

interface ShareButtonProps {
  title: string;
  text?: string;
  path?: string;
  className?: string;
}

/**
 * Share control with social-media targets (X, Facebook, Reddit, WhatsApp,
 * Telegram, LinkedIn, Email) plus copy-link and native share. Opens a
 * lightweight popover so users can pick a network directly.
 */
export function ShareButton({ title, text, path, className }: ShareButtonProps) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "copied" | "shared" | "error">("idle");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function buildUrl() {
    if (typeof window === "undefined") return path ?? "";
    return path
      ? `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`
      : window.location.href;
  }

  async function copyLink() {
    const url = buildUrl();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setStatus("copied");
    } catch {
      window.prompt("Copy this link:", url);
      setStatus("copied");
    }
    setOpen(false);
    window.setTimeout(() => setStatus("idle"), 2200);
  }

  async function nativeShare() {
    const url = buildUrl();
    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share({ title, text: text ?? title, url });
        setStatus("shared");
        setOpen(false);
        window.setTimeout(() => setStatus("idle"), 2200);
        return true;
      }
    } catch (err) {
      if ((err as DOMException)?.name === "AbortError") return true;
      // fall through to copy
    }
    return false;
  }

  async function handleMainClick() {
    // Use the platform share sheet whenever the browser provides one. Desktop
    // browsers without it retain the full social picker and clipboard fallback.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      const ok = await nativeShare();
      if (ok) return;
    }
    setOpen((v) => !v);
  }

  const url = typeof window !== "undefined" ? buildUrl() : "";
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(text ?? title);

  const targets: Array<{
    key: string;
    label: string;
    href: string;
    icon: ReactNode;
  }> = [
    {
      key: "x",
      label: "X / Twitter",
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2H21l-6.52 7.45L22 22h-6.828l-5.35-6.99L3.6 22H.844l6.98-7.98L2 2h6.914l4.84 6.4L18.244 2Zm-2.39 18.19h1.89L7.24 3.72H5.22l10.633 16.47Z"/></svg>,
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.47H15.2c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12Z"/></svg>,
    },
    {
      key: "reddit",
      label: "Reddit",
      href: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12.14a2.14 2.14 0 0 0-3.63-1.54c-1.42-.94-3.32-1.54-5.42-1.62l1.1-3.47 3.02.71a1.7 1.7 0 1 0 .18-1.03l-3.37-.79a.5.5 0 0 0-.59.34l-1.25 3.95c-2.13.07-4.07.66-5.51 1.61A2.14 2.14 0 1 0 3.8 13.9c-.02.18-.04.36-.04.55C3.76 17.5 7.44 20 12 20s8.24-2.5 8.24-5.55c0-.19-.02-.37-.04-.55.5-.39.8-1 .8-1.66ZM7.5 13.5a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 0 1-2.5 0Zm8.7 3.63c-1 .99-2.62 1.47-4.2 1.47s-3.2-.48-4.2-1.47a.5.5 0 1 1 .7-.72c.77.77 2.11 1.19 3.5 1.19s2.73-.42 3.5-1.19a.5.5 0 1 1 .7.72ZM15.75 14.75a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5Z"/></svg>,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.5 3.5A11 11 0 0 0 2.6 16.9L2 22l5.24-1.38A11 11 0 1 0 20.5 3.5ZM12 20a8 8 0 0 1-4.08-1.12l-.3-.17-3.11.82.83-3.03-.2-.31A8 8 0 1 1 12 20Zm4.4-5.98c-.24-.12-1.42-.7-1.64-.78-.22-.08-.38-.12-.54.12s-.62.78-.76.94c-.14.16-.28.18-.52.06a6.5 6.5 0 0 1-1.91-1.18 7.2 7.2 0 0 1-1.32-1.65c-.14-.24 0-.36.1-.48.1-.1.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.48-.4-.42-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.7 2.6 4.12 3.64.58.24 1.02.38 1.36.48.58.18 1.1.16 1.52.1.46-.06 1.42-.58 1.62-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z"/></svg>,
    },
    {
      key: "telegram",
      label: "Telegram",
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9.78 15.34 9.6 19c.34 0 .49-.15.66-.32l1.6-1.53 3.31 2.42c.61.34 1.05.16 1.21-.56l2.19-10.27c.2-.9-.33-1.26-.92-1.04L4.3 12.06c-.88.34-.87.83-.15 1.05l3.44 1.07 7.98-5.02c.37-.24.72-.11.44.14"/></svg>,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.71h.05c.53-1 1.83-2.06 3.76-2.06 4.02 0 4.76 2.64 4.76 6.08V21h-4v-5.36c0-1.28-.02-2.93-1.78-2.93-1.78 0-2.05 1.4-2.05 2.84V21h-4V9Z"/></svg>,
    },
    {
      key: "discord",
      label: "Discord",
      href: `https://discord.com/channels/@me`,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.3 4.5A16.6 16.6 0 0 0 16.2 3l-.3.6a12.4 12.4 0 0 1 3.6 1.8 11.6 11.6 0 0 0-9-1.1c-.5.1-1 .3-1.5.5.4-.2 1.2-.6 2.2-.9L11 3a16.6 16.6 0 0 0-4.1 1.5C4.4 8.4 3.4 12.6 3.7 17c1.6 1.2 3.2 1.9 4.7 2.4l.9-1.5c-.6-.2-1.2-.5-1.8-.9l.4-.3a12 12 0 0 0 8.2 0l.4.3c-.6.4-1.2.7-1.8.9l.9 1.5c1.5-.5 3-1.2 4.7-2.4.4-4.7-.7-8.9-3-12.5ZM9.2 14.5c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.7.8 1.6 1.8c0 1-.7 1.8-1.6 1.8Zm5.6 0c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.7.8 1.6 1.8c0 1-.7 1.8-1.6 1.8Z"/></svg>,
    },
    {
      key: "email",
      label: "Email",
      href: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>,
    },
  ];


  const label =
    status === "copied" ? "Link Copied!" :
    status === "shared" ? "Shared!" :
    status === "error" ? "Copy failed" : "Share";

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={handleMainClick}
        data-title={title}
        data-text={text ?? "Check out this Palworld TCG data on Pal Stars!"}
        data-url={path}
        aria-label={`Share ${title}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          className ??
          "inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
        }
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        {label}
      </button>

      {status === "copied" ? (
        <span
          role="status"
          className="share-tooltip pointer-events-none absolute bottom-full left-1/2 z-[60] mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-semibold text-background shadow-lg"
        >
          Link Copied!
        </span>
      ) : null}

      {open && (
        <div
          role="menu"
          className="absolute z-50 mt-2 right-0 w-56 rounded-xl border bg-background/95 backdrop-blur p-1.5 shadow-xl"
        >
          {targets.map((t) => (
            <a
              key={t.key}
              href={t.href}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-card"
            >
              <span className="text-primary">{t.icon}</span>
              <span>{t.label}</span>
            </a>
          ))}
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              await copyLink();
              window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
            }}
            className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-card text-left"
          >
            <span className="text-primary">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.7 3.7 0 0 1-.9 1.38 3.7 3.7 0 0 1-1.38.9c-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2Zm0 1.8c-3.15 0-3.5.01-4.75.07-1 .04-1.54.21-1.9.35-.48.19-.82.41-1.18.77a3.2 3.2 0 0 0-.77 1.18c-.14.36-.31.9-.35 1.9C3 8.5 3 8.85 3 12s.01 3.5.07 4.75c.04 1 .21 1.54.35 1.9.19.48.41.82.77 1.18.36.36.7.58 1.18.77.36.14.9.31 1.9.35C8.5 21 8.85 21 12 21s3.5-.01 4.75-.07c1-.04 1.54-.21 1.9-.35.48-.19.82-.41 1.18-.77.36-.36.58-.7.77-1.18.14-.36.31-.9.35-1.9.06-1.25.07-1.6.07-4.73s-.01-3.5-.07-4.75c-.04-1-.21-1.54-.35-1.9a3.2 3.2 0 0 0-.77-1.18 3.2 3.2 0 0 0-1.18-.77c-.36-.14-.9-.31-1.9-.35C15.5 4.01 15.15 4 12 4Zm0 3.05a4.95 4.95 0 1 1 0 9.9 4.95 4.95 0 0 1 0-9.9Zm0 1.8a3.15 3.15 0 1 0 0 6.3 3.15 3.15 0 0 0 0-6.3Zm5.15-2.2a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3Z"/></svg>
            </span>
            <span>Instagram (copies link)</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              await copyLink();
              window.open("https://www.tiktok.com/", "_blank", "noopener,noreferrer");
            }}
            className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-card text-left"
          >
            <span className="text-primary">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.6 6.7a5.6 5.6 0 0 1-3.4-1.2 5.6 5.6 0 0 1-2-3.5h-3.1v12.5a2.6 2.6 0 1 1-2.6-2.6c.28 0 .55.04.8.12V8.9a5.7 5.7 0 1 0 4.9 5.65V9.62a8.7 8.7 0 0 0 5.4 1.87V8.4c-.02 0-.01 0 0-1.7Z"/></svg>
            </span>
            <span>TikTok (copies link)</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={copyLink}
            className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-card text-left"
          >
            <span className="text-primary">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
            </span>
            <span>Copy link</span>
          </button>
          {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { void nativeShare(); }}
              className="w-full flex items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-card text-left"
            >
              <span className="text-primary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
              </span>
              <span>More…</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
